import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache, Observable } from '@apollo/client';
import { fromPromise } from '@apollo/client/link/utils';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { Theme } from '@radix-ui/themes';
import { useState } from 'react';
import { toast, Toaster } from 'sonner';
import type { Subscription } from 'zen-observable-ts';
import { SortPreferenceContext } from './SortPreferenceContext';
import { fetchAccessToken, getAccessToken, setAccessToken } from './components/accesstoken';
import type { GraphQLErrorWithCodeAndReason } from './components/pages/Error';
import { config } from './config';
import { trackError } from './analytics/track';
import { i18n } from './i18n';
import Layout from './layout/Layout';
import './style/css/custom.css';

const url = config.VITE_GRAPHQL_URL || 'https://backapp.hubert.ai';

if (config.PROD) {
  console.log = function () {};
  // removes console.log
}

const uri = url + '/graphql';

const cache = new InMemoryCache({
  typePolicies: {
    User: {
      fields: {
        pinnedJobs: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    Job: {
      fields: {
        location: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
    },
  },
});

const requestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle: Subscription;
      Promise.resolve(operation)
        .then((operation) => {
          const accessToken = getAccessToken();
          if (accessToken) {
            operation.setContext({
              headers: {
                authorization: `bearer ${accessToken}`,
              },
            });
          }
        })
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    }),
);

let isRefreshingToken = false;
let pendingRequests: ((accessToken: string) => void)[] = [];

const resolvePendingRequests = (accessToken: string) => {
  pendingRequests.forEach((callback) => callback(accessToken));
  pendingRequests = [];
};

const refreshAccessToken = () => {
  if (isRefreshingToken)
    return new Promise<string>((resolve) => {
      pendingRequests.push(resolve);
    });
  isRefreshingToken = true;
  return fetchAccessToken()
    .then(({ accessToken, ok }) => {
      setAccessToken(accessToken);

      if (!ok) {
        throw new Error('Failed to refresh token');
      }
      resolvePendingRequests(accessToken);
      return accessToken;
    })
    .catch((error) => {
      console.error('Failed to refresh token', error);
      pendingRequests = [];
      location.reload();
      throw error;
    })
    .finally(() => {
      isRefreshingToken = false;
    });
};

const ignoredErrorPaths = [
  'ForgotPasswordMagicLink',
  'loginResolverMutation',
  'changePassword',
  'InviteToOrganization',
  'ReinviteToOrganization',
  'AccountActivationMutation',
];
const errorLink = onError((errors) => {
  const { graphQLErrors, networkError, operation, forward } = errors;
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const error = err as GraphQLErrorWithCodeAndReason;
      if ((error.extensions?.code as string | undefined) === 'JOB_STAT_ERROR') {
        return;
      }
      if (error.code === 'FORBIDDEN') {
        trackError('graphql', 403);
        toast.error(error.message);
        return;
      }
      if (error.code === 'UNAUTHORIZED') {
        return fromPromise(refreshAccessToken()).flatMap((accessToken) => {
          operation.setContext(({ headers = {} }) => ({
            headers: { ...headers, Authorization: `bearer ${accessToken}` },
          }));
          return forward(operation);
        });
      } else if (error.path && ignoredErrorPaths.includes(`${error.path[0]}`)) {
        return;
      } else {
        trackError('graphql');
        toast.error(i18n.t('app.globalError.title'), { description: error.message });
      }
      console.log(`[GraphQL error]: ${JSON.stringify(error)}`);
    }
  }
  if (networkError) {
    const status = 'statusCode' in networkError ? (networkError as { statusCode?: number }).statusCode : undefined;
    trackError('graphql', status);
    console.warn(`[Network error]: ${networkError}`, { networkError, operation: errors.operation });
  }
});

const client = new ApolloClient({
  link: ApolloLink.from([
    new RetryLink({
      attempts: {
        max: 5,
        retryIf: async (error) => {
          if (error.statusCode && (error.statusCode === 403 || error.statusCode === 407)) return false;
          return true;
        },
      },
    }),
    errorLink,
    requestLink,
    new HttpLink({
      uri: uri,
      credentials: 'include',
    }),
  ]),
  cache,
});

export default function App() {
  return (
    <>
      <ApolloProvider client={client}>
        <SortPreferenceContext value={useState('score')}>
          <Theme>
            <Layout />
          </Theme>
        </SortPreferenceContext>
      </ApolloProvider>
      <Toaster richColors position="bottom-center" closeButton />
    </>
  );
}
